import { CloseCircleOutlined, DeleteOutlined } from "@ant-design/icons";
import { Button, Form, Input, Select, Typography } from "../../../../../shared/frontend/ui";
import React from "react";
import type { ApiClient } from "../app/api/client";
import type { I18nContextValue } from "../app/i18n/useI18n";
import { PAYMENT_METHOD_SEPA } from "../app/utils/paymentMethodCodes";
import { paymentMethodLabel } from "../app/utils/paymentMethod";
import { FormDrawer } from "./FormDrawer";
import type { Feedback } from "../../../../../shared/frontend/feedback/useFeedback";

/** Beträge unter einem halben Cent gelten als beglichen. */
const MONEY_EPSILON = 0.005;

export type ArticleSaleEditTarget = {
  id: string;
  openItemId?: string | null;
  paymentMethod?: number | null;
  /** Lastschriftgruppe des Belegs; entscheidet, in welchem Lauf die Ware eingezogen wird. */
  sepaGroupId?: string | null;
  /** Bereits gezahlter Betrag; entscheidet über „löschen" (nie gezahlt) statt „stornieren". */
  paidAmount?: number;
  canCancel: boolean;
};

type PaymentMethodDefinitionDto = {
  paymentMethodType: number;
  name: string;
  isActive: boolean;
};

type SepaGroupDto = { id: string; name: string; isActive: boolean };

type Props = {
  target: ArticleSaleEditTarget | null;
  api: Pick<ApiClient, "get" | "post" | "put">;
  message: Feedback;
  text: I18nContextValue["text"];
  canEditPayment: boolean;
  onClose: () => void;
  onSaved: () => void;
};

/**
 * Gemeinsamer Bearbeitungseinstieg für offene Artikelverkäufe. Die Artikel- und
 * Betragszeilen bleiben als Beleginhalt unveränderlich; die Zahlungsart darf bis
 * zur Zahlung bzw. Einreichung in einen Lastschriftlauf angepasst werden.
 */
export function ArticleSaleEditDrawer(props: Props) {
  if (!props.target) return null;
  return <ArticleSaleEditDrawerContent key={props.target.id} {...props} target={props.target} />;
}

function ArticleSaleEditDrawerContent({ target, api, message, text, canEditPayment, onClose, onSaved }: Omit<Props, "target"> & { target: ArticleSaleEditTarget }) {
  const [initialPayment] = React.useState(() => ({ method: target.paymentMethod == null ? undefined : Number(target.paymentMethod), sepaGroupId: target.sepaGroupId || undefined }));
  const [paymentMethods, setPaymentMethods] = React.useState<PaymentMethodDefinitionDto[]>([]);
  const [paymentMethod, setPaymentMethod] = React.useState<number | undefined>(() => target.paymentMethod == null ? undefined : Number(target.paymentMethod));
  const [sepaGroups, setSepaGroups] = React.useState<SepaGroupDto[]>([]);
  const [sepaGroupId, setSepaGroupId] = React.useState<string | undefined>(() => target.sepaGroupId || undefined);
  const [submitting, setSubmitting] = React.useState(false);
  const [cancellationOpen, setCancellationOpen] = React.useState(false);
  const [cancellationReason, setCancellationReason] = React.useState("");
  const [cancelling, setCancelling] = React.useState(false);

  // Auf den Beleg wurde nie gezahlt -> fachlich eine Löschung: der Verkauf verschwindet aus der Liste.
  // Sobald Geld geflossen ist, gilt der Beitragsweg: Beleg bleibt sichtbar, Betrag läuft über Guthaben/Korrektur.
  const isPaid = Number(target.paidAmount || 0) > MONEY_EPSILON;

  // Zahlarten und SEPA-Gruppen füllen die beiden Auswahlfelder. Ohne Ladekennzeichen standen die
  // Felder beim Öffnen leer und anklickbar da.
  // Startwert statt `setLookupsLoading(true)` im Effekt: Diese Komponente wird pro Öffnung neu
  // montiert (der Aufrufer rendert sie bedingt, `open` steht fest auf true). Ein synchrones
  // setState im Effekt-Rumpf wäre hier also nicht nur unnötig, sondern ein Lint-Verstoß
  // (`react-hooks/set-state-in-effect`).
  const [lookupsLoading, setLookupsLoading] = React.useState(canEditPayment);
  React.useEffect(() => {
    if (!canEditPayment) return;
    let alive = true;
    void Promise.all([
      api.get<PaymentMethodDefinitionDto[]>("/api/v1/payment-method-definitions")
        .then((items) => { if (alive) setPaymentMethods((items || []).filter((item) => item.isActive)); })
        .catch(() => { if (alive) setPaymentMethods([]); }),
      api.get<SepaGroupDto[]>("/api/v1/sepa-groups")
        .then((groups) => { if (alive) setSepaGroups((groups || []).filter((group) => group.isActive)); })
        .catch(() => { if (alive) setSepaGroups([]); })
    ]).finally(() => { if (alive) setLookupsLoading(false); });
    return () => { alive = false; };
  }, [api, canEditPayment]);

  return (
    <>
      <FormDrawer
        open
        dirty={paymentMethod !== initialPayment.method || sepaGroupId !== initialPayment.sepaGroupId}
        title="Edit article sale"
        formId="article-sale-edit-form"
        submitting={submitting}
        contentLoading={lookupsLoading}
        hideSubmit={!canEditPayment}
        submitDisabled={!target?.openItemId || paymentMethod == null}
        onCancel={() => {
          if (!submitting) onClose();
        }}
        extra={
          target?.canCancel ? (
            <Button
              danger
              icon={isPaid ? <CloseCircleOutlined /> : <DeleteOutlined />}
              onClick={() => setCancellationOpen(true)}
            >
              {isPaid ? text("Article sale void action") : text("Article sale delete action")}
            </Button>
          ) : null
        }
      >
        <Form
          id="article-sale-edit-form"
          layout="vertical"
          requiredMark={false}
          onFinish={async () => {
            if (!target?.openItemId || paymentMethod == null) return;
            setSubmitting(true);
            try {
              await api.put(`/api/v1/open-items/${encodeURIComponent(target.openItemId)}/payment-method`, { paymentMethod });
              // Reihenfolge ist fachlich bindend: Das Backend nimmt eine Lastschriftgruppe nur an,
              // wenn der Beleg bereits auf Lastschrift steht. Wechselt der Beleg auf eine andere
              // Zahlungsart, wird die Gruppe geleert, damit kein Lauf gegen eine tote Gruppe zieht.
              const nextSepaGroupId = paymentMethod === PAYMENT_METHOD_SEPA ? (sepaGroupId || null) : null;
              if (nextSepaGroupId !== (target.sepaGroupId || null)) {
                await api.put(`/api/v1/article-sales/${encodeURIComponent(target.id)}/sepa-group`, { sepaGroupId: nextSepaGroupId });
              }
              message.success(text("Saved."));
              onSaved();
              onClose();
            } catch {
              // Central API toast shows the typed server error.
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {canEditPayment ? (
            <Form.Item label={text("Payment method")} required>
              <Select
                value={paymentMethod}
                placeholder={text("Payment method")}
                onChange={(value) => setPaymentMethod(value != null ? Number(value) : undefined)}
                options={paymentMethods.map((method) => ({
                  value: Number(method.paymentMethodType),
                  label: paymentMethodLabel(method.paymentMethodType),
                }))}
              />
            </Form.Item>
          ) : null}
          {canEditPayment && paymentMethod === PAYMENT_METHOD_SEPA ? (
            <Form.Item
              label={text("Direct debit group")}
              extra={text("Article sale sepa group hint")}
            >
              <Select
                allowClear
                value={sepaGroupId}
                placeholder={text("Direct debit group")}
                onChange={(value) => setSepaGroupId(value || undefined)}
                options={sepaGroups.map((group) => ({ value: group.id, label: group.name }))}
              />
            </Form.Item>
          ) : null}
        </Form>
      </FormDrawer>

      <FormDrawer
        open={cancellationOpen}
        dirty={cancellationReason !== ""}
        title={isPaid ? "Cancel article sale" : "Delete article sale"}
        submitting={cancelling}
        dangerous
        submitText={isPaid ? "Confirm article sale void" : "Confirm article sale delete"}
        onCancel={() => {
          if (!cancelling) setCancellationOpen(false);
        }}
        onSave={async () => {
          if (!target) return;
          setCancelling(true);
          try {
            await api.post(`/api/v1/article-sales/${encodeURIComponent(target.id)}/cancel`, {
              reason: cancellationReason.trim() || null,
            });
            message.success(text("Saved."));
            setCancellationOpen(false);
            onSaved();
            onClose();
          } catch {
            // Central API toast shows the typed server error.
          } finally {
            setCancelling(false);
          }
        }}
      >
        <Form layout="vertical" requiredMark={false}>
          <Form.Item>
            <Typography.Text type="secondary">
              {isPaid ? text("Article sale void hint") : text("Article sale delete hint")}
            </Typography.Text>
          </Form.Item>
          <Form.Item label={text("Reason")}>
            <Input.TextArea
              rows={4}
              value={cancellationReason}
              placeholder={text("Reason (optional)")}
              onChange={(event) => setCancellationReason(event.target.value)}
            />
          </Form.Item>
        </Form>
      </FormDrawer>
    </>
  );
}
